import forge from 'apis/forge';
import { SceneComment } from 'dragonmouse-shared/interfaces';
import { generateKeyBetween } from 'fractional-indexing';
import { useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectSceneComments, selectUserSettings } from 'redux/db/reducers';
import { setCommentReorderPanelExpanded } from 'redux/mode/actions';
import { openCanvasLeft, openCanvasWidth } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

const COMMENT_DRAG_TYPE = 'SCENE_COMMENT';

interface DragItem {
	id: string;
	index: number;
}

//----------------------------------------------------------------------
// DRAGGABLE ROW
//----------------------------------------------------------------------

interface DraggableRowProps {
	comment: SceneComment;
	index: number;
	comments: SceneComment[];
}

const DraggableRow = ({ comment, index, comments }: DraggableRowProps) => {
	const ref = useRef<HTMLDivElement>(null);

	const [{ isDragging }, drag] = useDrag({
		type: COMMENT_DRAG_TYPE,
		item: (): DragItem => ({ id: comment.id, index }),
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [, drop] = useDrop<DragItem>({
		accept: COMMENT_DRAG_TYPE,
		drop: (item) => {
			if (item.id === comment.id) return;
			// Compute sortOrder to place the dragged item at this position.
			// After drop, the dragged item should end up at `index`.
			// We need the sortOrders of the neighbors at that position (excluding the dragged item).
			const filtered = comments.filter((c) => c.id !== item.id);
			const targetIdx = Math.min(index, filtered.length);
			const prevOrder = targetIdx > 0 ? (filtered[targetIdx - 1].metadata?.sortOrder ?? null) : null;
			const nextOrder = targetIdx < filtered.length ? (filtered[targetIdx].metadata?.sortOrder ?? null) : null;
			const newSortOrder = generateKeyBetween(prevOrder, nextOrder);
			const draggedComment = comments.find((c) => c.id === item.id);
			if (!draggedComment) return;
			forge.updateSceneComment({
				id: item.id,
				metadata: { ...draggedComment.metadata, sortOrder: newSortOrder },
			});
		},
	});

	drag(drop(ref));

	const firstLine = comment.text.split('\n')[0].slice(0, 40);

	return (
		<S.Row ref={ref} $isDragging={isDragging}>
			<S.DragHandle>&#x2630;</S.DragHandle>
			<S.RowNumber>{index + 1}</S.RowNumber>
			<S.RowText>{firstLine}</S.RowText>
		</S.Row>
	);
};

//----------------------------------------------------------------------
// MAIN COMPONENT
//----------------------------------------------------------------------

const CommentReorderPanel = () => {
	const dispatch = useDispatch();

	const { comments, expanded, commentEditingEnabled, panelRight } = useSelector(
		(r: RootReducer) => ({
			comments: selectSceneComments(r.db),
			expanded: r.mode.commentReorderPanelExpanded,
			commentEditingEnabled: selectUserSettings(r.db).commentEditingEnabled,
			panelRight: openCanvasLeft(r.mode) + openCanvasWidth(r.mode),
		}),
		shallowEqual
	);

	const onToggle = useCallback(() => {
		dispatch(setCommentReorderPanelExpanded(!expanded));
	}, [dispatch, expanded]);

	if (!commentEditingEnabled || comments.length === 0) return null;

	const rightOffset = panelRight > 0 ? window.innerWidth - panelRight + 16 : 16;

	return (
		<S.Base style={{ right: rightOffset }}>
			<S.ToggleButton onClick={onToggle}>
				{expanded ? 'Hide' : `Comments (${comments.length})`}
			</S.ToggleButton>
			{expanded && (
				<S.Panel>
					{comments.map((comment, index) => (
						<DraggableRow
							key={comment.id}
							comment={comment}
							index={index}
							comments={comments}
						/>
					))}
				</S.Panel>
			)}
		</S.Base>
	);
};

export default CommentReorderPanel;
